/**
 * PURPOSE: Bandeja de alertas SISDEU de la compañía (FTR-0017 Phase 4, D7/D4) — un mecanismo nuevo,
 *   separado a propósito de la campana de auditoría global (ver FTR-0017 "Current behavior": esa campana
 *   exige rol 1 global, no distingue compañía y no permite navegar a un registro concreto).
 * RESPONSIBILITIES: Filtrar por abiertas/resueltas/todas, deduplicar defensivamente, resolver una alerta,
 *   explicar SISDEU_INSTITUTION_CHANGED y SISDEU_DUPLICATE_CONFLICT en lenguaje llano y sin dato sensible,
 *   y navegar a la cuenta u opción de revisión de vínculos afectada cuando el backend lo permite
 *   identificar. El aislamiento por compañía lo aplica el backend (X-Hayllis-Company); esta pantalla solo
 *   refleja lo que el backend ya filtró.
 * COLLABORATORS: sisdeuAdminService.js, sisdeuAdmin.utils.js, CompanyInstitutionsTab.jsx
 *   (onNavigateToAccount abre "Accesos" en la cuenta señalada).
 * ROUTE: winscore/src/admin/AdminCompany/SisdeuAlertsModal.jsx
 */
import React, { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";

import { PORTAL_ALERT_STATUSES, listPortalAlerts, resolvePortalAlert } from "../../services/sisdeuAdminService.js";
import {
  dedupeAlertsById,
  describeAlert,
  getAlertTypeLabel,
  getSisdeuAdminErrorMessage,
  isCompanyAdminForbiddenError,
  isDelegatePortalUnavailableError,
  resolveAlertInstitutionId,
} from "./sisdeuAdmin.utils.js";

const STATUS_LABELS = { open: "Abiertas", resolved: "Resueltas", all: "Todas" };

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

const SisdeuAlertsModal = ({ onClose, onNavigateToAccount, onChanged }) => {
  const [status, setStatus] = useState("open");
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notice, setNotice] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);

  const load = useCallback(async (nextStatus) => {
    setLoading(true);
    setNotice(null);
    try {
      const { alerts: rows } = await listPortalAlerts(nextStatus);
      setAlerts(dedupeAlertsById(rows));
    } catch (error) {
      if (isCompanyAdminForbiddenError(error) || isDelegatePortalUnavailableError(error)) {
        setNotice({ tone: "forbidden", message: getSisdeuAdminErrorMessage(error, "No tienes acceso a esta sección.") });
      } else {
        setNotice({ tone: "error", message: getSisdeuAdminErrorMessage(error, "No se pudieron cargar las alertas.") });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(status);
  }, [status, load]);

  const handleResolve = async (alert) => {
    if (!window.confirm("¿Marcar esta alerta como resuelta?")) return;
    setResolvingId(alert.id);
    try {
      await resolvePortalAlert(alert.id);
      toast.success("Alerta resuelta.");
      await load(status);
      onChanged?.();
    } catch (error) {
      toast.error(getSisdeuAdminErrorMessage(error, "No se pudo resolver la alerta."));
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/50 p-4" onMouseDown={onClose}>
      <div
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onMouseDown={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sisdeu-alerts-title"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h3 id="sisdeu-alerts-title" className="text-xl font-bold text-slate-800">
              Alertas SISDEU
            </h3>
            <p className="text-sm text-slate-500">Bandeja compartida de esta compañía: cualquier administrador puede resolverlas.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full px-3 py-1 text-2xl leading-none text-slate-500 hover:bg-slate-100"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="mb-4 flex gap-2" role="tablist" aria-label="Filtro de alertas">
          {PORTAL_ALERT_STATUSES.map((value) => (
            <button
              key={value}
              type="button"
              role="tab"
              aria-selected={status === value}
              onClick={() => setStatus(value)}
              className={`rounded-lg px-3 py-1.5 text-xs font-semibold ${
                status === value ? "bg-blue-600 text-white" : "border border-slate-300 text-slate-600 hover:bg-slate-50"
              }`}
            >
              {STATUS_LABELS[value]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-10 text-center text-sm text-slate-500">Cargando...</div>
        ) : notice ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">{notice.message}</div>
        ) : alerts.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-300 py-10 text-center text-sm text-slate-500">
            No hay alertas {STATUS_LABELS[status].toLowerCase()}.
          </div>
        ) : (
          <ul className="space-y-3">
            {alerts.map((alert) => {
              const { title, lines } = describeAlert(alert);
              const institutionId = resolveAlertInstitutionId(alert);
              return (
                <li key={alert.id} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-slate-800">{title}</p>
                      <p className="text-xs text-slate-500">{getAlertTypeLabel(alert.type)} · {formatDate(alert.createdAt)}</p>
                    </div>
                    <span
                      className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                        alert.resolvedAt ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                      }`}
                    >
                      {alert.resolvedAt ? "Resuelta" : "Abierta"}
                    </span>
                  </div>
                  <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-slate-600">
                    {lines.map((line) => (
                      <li key={line}>{line}</li>
                    ))}
                  </ul>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {onNavigateToAccount && (institutionId != null || alert.accountId != null) && (
                      <button
                        type="button"
                        onClick={() => onNavigateToAccount({ idinstitution: institutionId, idaccount: alert.accountId })}
                        disabled={institutionId == null}
                        title={institutionId == null ? "El backend no identificó la institución de esta alerta." : undefined}
                        className="rounded-lg border border-blue-200 px-3 py-1.5 text-xs font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-40"
                      >
                        Ver cuenta
                      </button>
                    )}
                    {!alert.resolvedAt && (
                      <button
                        type="button"
                        onClick={() => handleResolve(alert)}
                        disabled={resolvingId === alert.id}
                        className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                      >
                        {resolvingId === alert.id ? "Resolviendo..." : "Marcar como resuelta"}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default SisdeuAlertsModal;
